/**
 * pv-model.js
 *
 * Modelul de calcul pentru calculatorul de fotovoltaice (casebune.ro).
 *
 * SINGURA SURSA DE ADEVAR pentru formule. Rulează identic:
 *  - în browser, ca script clasic, expune window.PVModel
 *  - în Node, prin tools/build-ssr.mjs, ca să genereze tabelul server-side
 *
 * Nu duplica logica asta în Liquid. Tabelul SSR se generează din aici.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.PVModel = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /**
   * Cele 5 scenarii. Fiecare are un mecanism de preț PROPRIU.
   * Atenție: scenariile 4 și 5 NU adaugă venit peste scenariul 3,
   * ele înlocuiesc complet mecanismul de preț.
   */
  var SCENARII = [
    { id: 'fara', nume: 'Fără panouri', pv: false, baterie: false, regim: 'clasic' },
    { id: 'pv', nume: 'Panouri, fără baterie', pv: true, baterie: false, regim: 'clasic' },
    { id: 'pv_bat', nume: 'Panouri + baterie', pv: true, baterie: true, regim: 'clasic' },
    { id: 'pv_bat_pzu', nume: 'Panouri + baterie + tarif PZU', pv: true, baterie: true, regim: 'pzu' },
    { id: 'pv_bat_vpp', nume: 'Panouri + baterie + YellowGrid', pv: true, baterie: true, regim: 'vpp' }
  ];

  function clamp(v, min, max) {
    return Math.min(Math.max(v, min), max);
  }

  /**
   * Prețul plătit pe un kWh LUAT din rețea, all-in, pe regim.
   * Podeaua reglementată (rețea + taxe) se aplică în TOATE regimurile,
   * inclusiv la prosumator. Asta e partea pe care nimeni nu o poate evita.
   */
  function pretImport(p, regim) {
    var podeaFaraTva = p.transportTL + p.serviciiSistem + p.distributieJT + p.taxeCertificateAcciza;

    var componentaEnergie;
    if (regim === 'pzu') {
      // Tarif indexat la PZU: media PZU + componenta fixă de furnizare.
      componentaEnergie = p.pzuMediu + p.pzuComponentaFixa;
    } else if (regim === 'vpp') {
      // ATENTIE, ipoteză de verificat: pragul YellowGrid de 0,55 lei/kWh este
      // interpretat ca COMPONENTA DE ENERGIE, nu ca preț all-in, fiindcă
      // podeaua reglementată singură depășește 0,55 lei/kWh.
      componentaEnergie = p.vppPretImportMax;
    } else {
      // Regim clasic: derivăm componenta de energie din prețul all-in declarat,
      // ca să rămână consistent cu ce plătește userul pe factură.
      return p.pretAllIn;
    }

    return (componentaEnergie + podeaFaraTva) * (1 + p.tva / 100);
  }

  /**
   * Valoarea unui kWh INJECTAT în rețea, pe regim.
   *
   * Miezul metodologic al calculatorului. Un kWh injectat NU valorează
   * cât unul autoconsumat, fiindcă:
   *  - clasic: compensarea cantitativă îl valorifică doar la componenta de energie
   *  - pzu: se valorifică la prețul PZU din momentul injecției, iar un sistem
   *    fără baterie injectează la amiază, când PZU e cel mai mic
   *  - vpp: preț contractual garantat
   */
  function valoareInjectie(p, regim, areBaterie) {
    if (regim === 'pzu') {
      // Fără baterie injectezi la amiază, adică în golul de preț.
      // Cu baterie poți muta o parte din export spre vârful de seară.
      var pretAmiaza = clamp(p.pzuAmiaza, p.pzuPragMin, p.pzuPlafon);
      if (!areBaterie) return pretAmiaza;
      var pretSeara = clamp(p.pzuSeara, p.pzuPragMin, p.pzuPlafon);
      var cotaSeara = p.cotaExportMutatSeara / 100;
      return pretAmiaza * (1 - cotaSeara) + pretSeara * cotaSeara;
    }
    if (regim === 'vpp') {
      // Prag garantat plus partea ta din upside-ul de tranzacționare.
      var upside = Math.max(0, p.vppPretExportRealizat - p.vppPretExportMin);
      return p.vppPretExportMin + upside * (p.vppCotaClient / 100);
    }
    // Clasic: compensare cantitativă, doar componenta de energie activă.
    return p.componentaEnergie;
  }

  /** Rata de autoconsum, în procente. */
  function rataAutoconsum(p, areBaterie) {
    return areBaterie ? p.autoconsumCuBaterie : p.autoconsumFaraBaterie;
  }

  /** Capex net, după subvenție. */
  function capex(p, sc) {
    if (!sc.pv) return 0;
    var total = p.costSistemPV;
    if (sc.baterie) total += p.costBaterie;
    return Math.max(0, total - p.subventie);
  }

  /**
   * Rulează un scenariu pe tot orizontul.
   * Întoarce fluxul anual plus metricile agregate.
   */
  function ruleazaScenariu(p, sc) {
    var ani = [];
    var cumulat = 0;
    var capexNet = capex(p, sc);
    var anAmortizare = null;
    var totalProdus = 0;
    var totalCostFaraPV = 0;
    var totalCostCuPV = 0;

    var rata = sc.pv ? rataAutoconsum(p, sc.baterie) / 100 : 0;
    var pImport = pretImport(p, sc.regim);
    var vInjectie = sc.pv ? valoareInjectie(p, sc.regim, sc.baterie) : 0;

    for (var n = 1; n <= p.orizont; n++) {
      var inflatie = Math.pow(1 + p.cresterePret / 100, n - 1);
      var pretImportAn = pImport * inflatie;
      var valInjectieAn = vInjectie * inflatie;

      // Referința: cât ai plăti fără panouri.
      var costFaraPV = p.consumAnual * pretImportAn;

      var productie = 0;
      var autoconsum = 0;
      var injectie = 0;
      var economieAutoconsum = 0;
      var venitInjectie = 0;
      var venitArbitraj = 0;
      var venitVPP = 0;

      if (sc.pv) {
        var degradare = Math.pow(1 - p.degradarePanouri / 100, n - 1);
        productie = p.kWp * p.productieSpecifica * degradare;
        autoconsum = Math.min(productie * rata, p.consumAnual);
        injectie = Math.max(0, productie - autoconsum);

        // Autoconsumul economisește prețul INTREG, fiindcă nu mai iei din rețea.
        economieAutoconsum = autoconsum * pretImportAn;

        // Compensarea cantitativă lucrează doar până la limita consumului rămas.
        var consumRamas = Math.max(0, p.consumAnual - autoconsum);
        var injectieCompensata = Math.min(injectie, consumRamas);
        var injectieExcedent = Math.max(0, injectie - injectieCompensata);

        // Excedentul de peste consum se valorifică la același preț de injecție,
        // dar fără să poată depăși consumul (nu poți compensa ce nu consumi).
        venitInjectie = (injectieCompensata + injectieExcedent) * valInjectieAn;

        if (sc.baterie) {
          if (sc.regim === 'pzu') {
            // Arbitraj: încarci ieftin, consumi la vârf.
            // ATENTIE: spread-ul captabil pe retail e mult mai mic decât cel wholesale.
            venitArbitraj = p.capacitateBaterie * p.spreadCaptabil *
              p.zileArbitraj * (p.randamentBaterie / 100) * inflatie;
          }
          if (sc.regim === 'vpp') {
            venitVPP = p.venitVPPAnual * inflatie;
          }
        }
      }

      var beneficiu = economieAutoconsum + venitInjectie + venitArbitraj + venitVPP;
      var costCuPV = Math.max(0, costFaraPV - beneficiu);

      var cheltuieli = 0;
      if (sc.pv) {
        cheltuieli += p.mentenantaAnuala;
        if (sc.baterie && n === p.anInlocuireBaterie) {
          cheltuieli += p.costBaterie * (p.costInlocuireBaterie / 100);
        }
      }

      var net = beneficiu - cheltuieli;
      cumulat += net;

      if (anAmortizare === null && capexNet > 0 && cumulat >= capexNet) {
        anAmortizare = n;
      }

      totalProdus += productie;
      totalCostFaraPV += costFaraPV;
      totalCostCuPV += costCuPV + cheltuieli;

      ani.push({
        an: n,
        productie: productie,
        autoconsum: autoconsum,
        injectie: injectie,
        economieAutoconsum: economieAutoconsum,
        venitInjectie: venitInjectie,
        venitArbitraj: venitArbitraj,
        venitVPP: venitVPP,
        beneficiu: beneficiu,
        cheltuieli: cheltuieli,
        cumulat: cumulat,
        facturaRamasa: costCuPV
      });
    }

    var economieNeta = totalCostFaraPV - totalCostCuPV - capexNet;
    var lcoe = totalProdus > 0 ? (capexNet + p.mentenantaAnuala * p.orizont) / totalProdus : 0;

    // Economia în valoare de azi. Cifra nominală pe 25 de ani arată spectaculos
    // fiindcă adună lei din 2051 cu lei din 2026. Valoarea actualizată e cea
    // onestă și e singura care se compară corect cu investiția de azi.
    var economieNetaActualizata = -capexNet;
    for (var k = 0; k < ani.length; k++) {
      var netK = ani[k].beneficiu - ani[k].cheltuieli;
      economieNetaActualizata += netK / Math.pow(1 + p.rataActualizare / 100, ani[k].an);
    }

    return {
      id: sc.id,
      nume: sc.nume,
      capex: capexNet,
      anAmortizare: anAmortizare,
      amortizareActualizata: amortizareActualizata(ani, capexNet, p.rataActualizare),
      economieNeta: economieNeta,
      economieNetaActualizata: economieNetaActualizata,
      lcoe: lcoe,
      totalProdus: totalProdus,
      facturaLunaraMedie: totalCostCuPV / (p.orizont * 12),
      pretImport: pImport,
      valoareInjectie: vInjectie,
      ani: ani
    };
  }

  /**
   * Amortizarea MARGINALA a unui scenariu față de altul.
   *
   * Asta e metrica care răspunde la întrebarea reală a omului. Nimeni nu
   * întreabă „se amortizează tot sistemul", ci „merită să mai dau bani pe
   * baterie". Diferența de investiție împărțită la diferența de beneficiu.
   *
   * Fără metrica asta, un tabel cu amortizări totale ascunde exact răspunsul,
   * fiindcă bateria pare aproape gratuită pe lângă beneficiul panourilor.
   */
  function amortizareMarginala(scenariu, referinta, rataActualizare) {
    var capexSuplimentar = scenariu.capex - referinta.capex;
    if (capexSuplimentar <= 0) return { capexSuplimentar: 0, aniSimplu: null, aniActualizat: null };

    var cumulat = 0;
    var cumulatAct = 0;
    var aniSimplu = null;
    var aniActualizat = null;
    var n = Math.min(scenariu.ani.length, referinta.ani.length);

    for (var i = 0; i < n; i++) {
      var netScenariu = scenariu.ani[i].beneficiu - scenariu.ani[i].cheltuieli;
      var netReferinta = referinta.ani[i].beneficiu - referinta.ani[i].cheltuieli;
      var delta = netScenariu - netReferinta;
      var an = scenariu.ani[i].an;

      cumulat += delta;
      cumulatAct += delta / Math.pow(1 + rataActualizare / 100, an);

      if (aniSimplu === null && cumulat >= capexSuplimentar) aniSimplu = an;
      if (aniActualizat === null && cumulatAct >= capexSuplimentar) aniActualizat = an;
    }

    return {
      capexSuplimentar: capexSuplimentar,
      aniSimplu: aniSimplu,
      aniActualizat: aniActualizat,
      beneficiuSuplimentarAn1:
        (scenariu.ani[0].beneficiu - scenariu.ani[0].cheltuieli) -
        (referinta.ani[0].beneficiu - referinta.ani[0].cheltuieli)
    };
  }

  /**
   * Amortizare cu actualizare, adică ținând cont de costul de oportunitate
   * al banilor. Amortizarea simplă supraestimează beneficiul, de aceea
   * raportăm ambele. Detaliul asta face pagina citabilă.
   */
  function amortizareActualizata(ani, capexNet, rata) {
    if (!capexNet) return null;
    var cumulat = 0;
    for (var i = 0; i < ani.length; i++) {
      var n = ani[i].an;
      var net = ani[i].beneficiu - ani[i].cheltuieli;
      cumulat += net / Math.pow(1 + rata / 100, n);
      if (cumulat >= capexNet) return n;
    }
    return null;
  }

  /**
   * Rulează toate scenariile și atașează metricile marginale.
   * Referința pentru marginal e scenariul cu panouri fără baterie, fiindcă
   * întrebarea reală e „merită bateria peste panouri", nu „merită panourile".
   */
  function ruleazaTot(params) {
    var p = params;
    var rezultate = SCENARII.map(function (sc) {
      return ruleazaScenariu(p, sc);
    });

    var refPV = rezultate.filter(function (r) { return r.id === 'pv'; })[0];
    if (refPV) {
      rezultate.forEach(function (r) {
        r.marginal = (r.id === 'fara' || r.id === 'pv')
          ? null
          : amortizareMarginala(r, refPV, p.rataActualizare);
      });
    }
    return rezultate;
  }

  /**
   * PRAGUL DE RENTABILITATE PENTRU VPP.
   *
   * Nu există nicio estimare independentă, pe gospodărie, a câștigului real
   * dintr-un VPP în România. Singura cifră publicată e marketingul furnizorului.
   * Deci nu afirmăm un venit, ci întoarcem întrebarea:
   *
   *   cât ar trebui să îți aducă VPP-ul pe an, ca bateria să se amortizeze
   *   în propria durată de viață?
   *
   * Asta transformă un gol de date într-un rezultat verificabil, și e cifra
   * cu care poți confrunta orice ofertă comercială.
   */
  function pragVenitVPP(params) {
    var termen = params.anInlocuireBaterie; // bateria trebuie să se plătească până la înlocuire
    var refPV = ruleazaScenariu(params, SCENARII.filter(function (s) { return s.id === 'pv'; })[0]);
    var scVPP = SCENARII.filter(function (s) { return s.id === 'pv_bat_vpp'; })[0];

    function seAmortizeazaLa(venit) {
      var p2 = {};
      for (var k in params) { if (Object.prototype.hasOwnProperty.call(params, k)) p2[k] = params[k]; }
      p2.venitVPPAnual = venit;
      var r = ruleazaScenariu(p2, scVPP);
      var m = amortizareMarginala(r, refPV, params.rataActualizare);
      return m.aniActualizat !== null && m.aniActualizat <= termen;
    }

    if (seAmortizeazaLa(0)) return 0;

    // Căutare binară pe venitul anual.
    var jos = 0, sus = 20000;
    if (!seAmortizeazaLa(sus)) return null; // nici cu 20.000 lei/an nu iese
    for (var i = 0; i < 40; i++) {
      var mij = (jos + sus) / 2;
      if (seAmortizeazaLa(mij)) sus = mij; else jos = mij;
    }
    return sus;
  }

  /**
   * Raportul care e teza paginii: de câte ori valorează mai mult
   * un kWh autoconsumat față de unul injectat, în regim clasic.
   */
  function raportAutoconsumInjectie(p) {
    var inj = p.componentaEnergie;
    return inj > 0 ? p.pretAllIn / inj : 0;
  }

  return {
    SCENARII: SCENARII,
    ruleazaScenariu: ruleazaScenariu,
    ruleazaTot: ruleazaTot,
    amortizareMarginala: amortizareMarginala,
    pragVenitVPP: pragVenitVPP,
    pretImport: pretImport,
    valoareInjectie: valoareInjectie,
    raportAutoconsumInjectie: raportAutoconsumInjectie
  };
});
